import React, { Component, Fragment, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import I18n from "i18n-js";

import api from "../../api";

import registrationSchema from "./validation/registration";

import LocaleSwitch from "../../commons/LocaleSwitch";

import Input from "./components/Input";
import CityInput from "./components/CityInput";
import StateInput from "./components/StateInput";
import CountryInput from "./components/CountryInput";
import TermsAndConditions from "./components/TermsAndConditions";

import logo from "images/logo/hey-vicky-white.svg";
import googleIcon from "images/google.svg";
import microsoftIcon from "images/microsoft.svg";

const SIMPLE_INPUTS = ["first_name", "last_name", "email", "password"];

const New = ({ locales }) => {

  const schema = registrationSchema(I18n.t("registration.form"));
  const { control, setValue, setError, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      country: "",
      state: "",
      city: "",
    },
    resolver: yupResolver(schema)
  });

  const [countryId, setCountryId] = useState();
  const [defaultCountry, setDefaultCountry] = useState("");
  const [stateId, setStateId] = useState();

  const [showTermsModal, setShowTermsModal] = useState(false);

  // By default, if location is "en", set country as USA,
  // If it is "pt-BR", set country to "Brasil".
  useEffect(() => {
    if (I18n.locale === "en") {
      setDefaultCountry("United States");
    } else if (I18n.locale == "pt-BR") {
      setDefaultCountry("Brasil");
    }
  }, []);

  const createUser = (data) => {
    api.post("/users", { user: data })
      .then(response => {
        const { messages, redirectTo, status } = response.data;
    
        if (status === 422) {
          Object.entries(messages).forEach(([key, value]) => {
            setError(key, { type: "server", message: value }, { shouldFocus: true });
          });
        } else {
          window.location.assign(redirectTo);
        }
      });
  }

  const showTerms = () => {
    setShowTermsModal(true);
  }

  const hideTerms = () => {
    setShowTermsModal(false);
  }

  const handleCountrySelected = (country) => {
    setCountryId(country.id);
    setValue("country", country.iso2, { shouldValidate: true });
  }

  const handleStateSelected = (state) => {
    setStateId(state.id);
    setValue("state", state.name, { shouldValidate: true });
  }

  const handleCitySelected = (city) => {
    setValue("city", city.name, { shouldValidate: true });
  }

  const renderField = (name, index) => (
    <Controller
      key={index}
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          name={name}
          placeholder={I18n.t(`registration.form.fields.${name}.placeholder`)}
          error={errors[name]?.message}
          {...field}
        />
      )}
    />
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit(createUser)}>
      {SIMPLE_INPUTS.map(renderField)}
      <CountryInput
        placeholder={I18n.t("registration.form.fields.country.placeholder")}
        defaultValue={defaultCountry}
        error={errors.country?.message}
        onChange={handleCountrySelected}
      />
      <StateInput
        countryId={countryId}
        placeholder={I18n.t("registration.form.fields.state.placeholder")}
        error={errors.state?.message}
        onChange={handleStateSelected}
      />
      <CityInput
        countryId={countryId}
        stateId={stateId}
        placeholder={I18n.t("registration.form.fields.city.placeholder")}
        error={errors.city?.message}
        onChange={handleCitySelected}
      />
      <input
        className="btn btn-primary btn-block submit-btn"
        type="submit"
        value={I18n.t("registration.form.actions.submit")}
      />
    </form>
  );

  return (
    <Fragment>
      <div className="registration-section">
        <div className="background-section">
          <img className="app-logo" src={logo} />
          <div className="background-content">
            <h1>
              {I18n.t("registration.hero")}
            </h1>
          </div>
        </div>
        <div className="form-section">
          <div className="registration-form">
            <div className="registration-content">
              <h2>{I18n.t("registration.title")}</h2>
              <div className="registration-content__message">
                {I18n.t("registration.description")}
              </div>
            </div>
            {renderForm()}
            <div className="terms-conditions">
              {I18n.t("registration.terms_policy.initial")}
              <span onClick={showTerms}>{I18n.t("registration.terms_policy.terms")}</span>&nbsp;
              {I18n.t("registration.terms_policy.and")}
              <span onClick={showTerms}>{I18n.t("registration.terms_policy.policy")}</span>.&nbsp;
              {I18n.t("registration.terms_policy.final")}
            </div>
            <div className="social-authentication">
              <div className="line" />
              <div className="text">{I18n.t("registration.sign_up_with_social")}</div>
              <div className="line" />
            </div>
            <div className="social-buttons">
              <form action="/users/auth/google_signin" method="post">
                <button type="submit" className="social-btn">
                  <img src={googleIcon} className="logo" alt="Google logo" />
                </button>
              </form>
              <form action="/users/auth/ms_signin" method="post">
                <button type="submit" className="social-btn">
                  <img src={microsoftIcon} className="logo" alt="Microsoft logo" />
                </button>
              </form>
            </div>
          </div>
        </div>
        <LocaleSwitch locales={locales} />
      </div>
      <TermsAndConditions show={showTermsModal} onHide={hideTerms} />
    </Fragment>
  );
}

// NOTE: needed now to current version of ReactOnRails to work with hooks
export default class NewComponent extends Component {
  render() {
    return <New {...this.props} />;
  }
}